import type React from 'react';
import { FolderOpen, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  withTruncation,
  type TruncationInjectedProps,
} from '@/components/shared/hocs/withTruncation';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { cn } from '@/lib/utils';
import type { AiInstructionDataTableItem } from '@kirin/api-client/contracts/ai-instructions';
import {
  getFilePathDisplayLabel,
  getVirtualPathDetails,
} from '@kirin/api-client/contracts';
import {
  QUARANTINED_BADGE_CLASS,
  QUARANTINED_BADGE_LABEL,
  QUARANTINED_TOOLTIP,
  TYPE_BADGE_CLASSES,
  getCategoryBadgeLabel,
} from './constants';
import { TextHighlight } from '@/components/shared/TextHighlight';
import { useDataTableSearch } from '@/components/DataTable/DataTableSearchContext';

// KIR-2548: display-only forward-slash conversion (backend data untouched).
function normalizePathForDisplay(path: string): string {
  return path.replace(/\\/g, '/');
}

interface CellProps {
  row: AiInstructionDataTableItem;
}

interface NameCellProps extends CellProps {
  onNameClick: (contentId: string) => void;
}

interface ProjectsCellProps extends CellProps {
  onProjectsClick: (contentId: string, fileCategory: string | null) => void;
}

interface UsersCellProps extends CellProps {
  onUsersClick: (contentId: string, fileCategory: string | null) => void;
}

// ── NameCell ──────────────────────────────────────────────
type TruncatedNameProps = NameCellProps &
  TruncationInjectedProps & { searchTerm: string };

const TruncatedName = withTruncation<TruncatedNameProps>(
  ({
    row,
    onNameClick,
    truncationRef,
    isTruncated,
    checkTruncation,
    searchTerm,
  }) => {
    const display = row.name ?? normalizePathForDisplay(row.filePath);

    const button = (
      <Button
        variant="link"
        className="text-sm font-medium text-primary hover:underline rounded-none max-w-full text-left h-auto p-0 justify-start min-w-0"
        onClick={() => onNameClick(row.contentId)}
      >
        <span
          ref={truncationRef}
          className="truncate block min-w-0 max-w-full"
          onMouseEnter={checkTruncation}
        >
          <TextHighlight text={display} searchTerm={searchTerm} />
        </span>
      </Button>
    );

    const name = !isTruncated ? (
      button
    ) : (
      <Tooltip>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {display}
        </TooltipContent>
      </Tooltip>
    );

    if (!row.quarantinedByKirin) {
      return name;
    }

    // KIR-3937: quarantine blanks the file on disk. Without this badge the row
    // reads as an ordinary empty file and the user has no way to learn that
    // Kirin was the one that emptied it.
    return (
      <span className="flex items-center gap-1.5 min-w-0">
        {name}
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className={cn(QUARANTINED_BADGE_CLASS, 'shrink-0')}
            >
              {QUARANTINED_BADGE_LABEL}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
            {QUARANTINED_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      </span>
    );
  }
);

export function NameCell({ row, onNameClick }: NameCellProps) {
  const searchTerm = useDataTableSearch();
  return (
    <TruncatedName
      row={row}
      onNameClick={onNameClick}
      searchTerm={searchTerm}
    />
  );
}

// ── TypeCell ──────────────────────────────────────────────
interface TruncatedTypeProps extends TruncationInjectedProps {
  badgeClass: string;
  label: string;
}

const TruncatedType = withTruncation<TruncatedTypeProps>(
  ({ badgeClass, label, truncationRef, isTruncated, checkTruncation }) => {
    const badge = (
      <Badge
        ref={truncationRef}
        variant="outline"
        className={cn(
          badgeClass,
          'max-w-full inline-block truncate align-middle'
        )}
        onMouseEnter={checkTruncation}
      >
        {label}
      </Badge>
    );

    if (!isTruncated) {
      return badge;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function TypeCell({ row }: CellProps): React.ReactElement {
  const category = row.fileCategory;
  if (!category)
    return <span className="text-muted-foreground text-sm">—</span>;

  const badgeClass =
    TYPE_BADGE_CLASSES[category] ?? 'bg-muted text-muted-foreground';
  const label = getCategoryBadgeLabel(category);
  return <TruncatedType badgeClass={badgeClass} label={label} />;
}

// ── FileCell ──────────────────────────────────────────────
// Virtual rows (e.g. `vscode://settings`) aren't real filesystem paths: they
// render a friendly label and expose the underlying URI + backing file via
// tooltip for traceability. KIR-2595.
type VirtualFileProps = CellProps & TruncationInjectedProps;

const VirtualFile = withTruncation<VirtualFileProps>(
  ({ row, truncationRef, checkTruncation }) => {
    const searchTerm = useDataTableSearch();
    const virtualDetails = getVirtualPathDetails(row.filePath);
    // VirtualFile is only mounted for virtual rows (see FileCell guard), so
    // `virtualDetails` is always present here.
    if (!virtualDetails) {
      return null;
    }
    const content = (
      <span
        ref={truncationRef}
        className="font-mono text-xs text-muted-foreground truncate block max-w-full"
        onMouseEnter={checkTruncation}
      >
        <TextHighlight
          text={getFilePathDisplayLabel(row.filePath)}
          searchTerm={searchTerm}
        />
      </span>
    );

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {`Source: ${virtualDetails.source}\nBacked by: ${virtualDetails.backedBy}`}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function FileCell({ row }: CellProps) {
  const searchTerm = useDataTableSearch();
  if (getVirtualPathDetails(row.filePath)) {
    return <VirtualFile row={row} />;
  }
  // KIR-2563: real paths use width-measured middle truncation. When a row spans
  // multiple paths, surface them all (copyable) via the popover body.
  const displayFilePaths = row.filePaths.map(normalizePathForDisplay);
  const copyText =
    displayFilePaths.length > 1 ? displayFilePaths.join('\n') : undefined;
  return (
    <MiddleTruncatedPath
      path={normalizePathForDisplay(row.filePath)}
      copyText={copyText}
      searchTerm={searchTerm}
      className="text-xs text-muted-foreground"
    />
  );
}

// ── SummaryCell ───────────────────────────────────────────
type TruncatedSummaryProps = CellProps & TruncationInjectedProps;

const TruncatedSummary = withTruncation<TruncatedSummaryProps>(
  ({ row, truncationRef, isTruncated, checkTruncation }) => {
    const searchTerm = useDataTableSearch();

    if (!row.summary) {
      const dash = <span className="text-muted-foreground text-sm">—</span>;
      // KIR-3937: an emptied file has nothing to summarize — say why rather
      // than leaving a bare dash the user cannot interpret.
      if (!row.quarantinedByKirin) {
        return dash;
      }
      return (
        <Tooltip>
          <TooltipTrigger asChild>{dash}</TooltipTrigger>
          <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
            {QUARANTINED_TOOLTIP}
          </TooltipContent>
        </Tooltip>
      );
    }

    const content = (
      <span
        ref={truncationRef}
        className="text-sm truncate block max-w-full"
        onMouseEnter={checkTruncation}
      >
        <TextHighlight text={row.summary} searchTerm={searchTerm} />
      </span>
    );

    if (!isTruncated) {
      return content;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-md whitespace-pre-wrap">
          {row.summary}
        </TooltipContent>
      </Tooltip>
    );
  }
);

export function SummaryCell({ row }: CellProps) {
  return <TruncatedSummary row={row} />;
}

// ── ProjectsCell ──────────────────────────────────────────
export function ProjectsCell({
  row,
  onProjectsClick,
}: ProjectsCellProps): React.ReactElement {
  const count = row.projects?.length ?? 0;

  if (count === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 gap-1.5 underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors"
      onClick={() => onProjectsClick(row.contentId, row.fileCategory ?? null)}
      title={`${count} project${count !== 1 ? 's' : ''} using this`}
    >
      <FolderOpen className="h-3.5 w-3.5" />
      {count}
    </Button>
  );
}

// ── UsersCell ─────────────────────────────────────────────
export function UsersCell({ row, onUsersClick }: UsersCellProps) {
  const count = row.users?.length ?? 0;

  if (count === 0) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 gap-1.5 underline decoration-primary/40 underline-offset-2 hover:decoration-primary transition-colors"
      onClick={() => onUsersClick(row.contentId, row.fileCategory ?? null)}
      title={`${count} user${count !== 1 ? 's' : ''} using this instruction`}
    >
      <Users className="h-3.5 w-3.5" />
      {count}
    </Button>
  );
}

// ── ActionsCell ───────────────────────────────────────────
export function ActionsCell() {
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Coming Soon
    </Badge>
  );
}
